/**
 * Frame Selector panel component
 */

import { useState, useMemo } from 'react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TemplateGrid,
  Slider,
  CollapsibleCard,
  cn,
  type TemplateItem,
} from '@frameit/ui';
import { useProjectStore, useSelectedArtwork } from '../../stores/projectStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAuthStore } from '../../stores/authStore';
import { ShadowControls } from './ShadowControls';
import { PriceCalculator } from './PriceCalculator';
import { AIRecommendations } from './AIRecommendations';
import { FRAMES, FRAME_CATEGORIES, createFrameThumbnail, getFrameData } from '../../data/frames';

export interface FrameSelectorProps {
  /** Custom class name */
  className?: string;
}

/** Frame selector component */
export function FrameSelector({ className }: FrameSelectorProps) {
  const selectedArtwork = useSelectedArtwork();
  const updateFrame = useProjectStore((s) => s.updateFrame);
  const updateDisplayScale = useProjectStore((s) => s.updateDisplayScale);
  const hasPremiumAccess = useAuthStore((s) => s.canUsePremiumTemplates());

  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  // Compute filtered frames from centralized data
  const frames = useMemo((): TemplateItem[] => {
    const allFrames: TemplateItem[] = FRAMES.map((f) => ({
      id: f.id,
      name: f.name,
      thumbnailUrl: createFrameThumbnail(
        f.color,
        '#f5f5f5',
        Math.min(14, Math.round(f.defaultWidth / 3)),
      ),
      category: f.category,
      isPremium: f.isPremium,
    }));

    const searchLower = search.toLowerCase().trim();
    return allFrames.filter((f) => {
      const matchesCategory = category === 'all' || f.category === category;
      const matchesSearch = !searchLower || f.name.toLowerCase().includes(searchLower);
      return matchesCategory && matchesSearch;
    });
  }, [category, search]);

  const handleFrameSelect = (template: TemplateItem) => {
    if (!selectedArtwork) return;
    const frameData = getFrameData(template.id);
    updateFrame(selectedArtwork.id, {
      templateId: template.id,
      width: frameData?.defaultWidth ?? 25,
    });
  };

  if (!selectedArtwork) {
    return (
      <div className={cn('p-4 text-center text-muted-foreground', className)}>
        Select an artwork to customize its frame
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Single scroll container for all content */}
      <div className="flex-1 overflow-auto">
        {/* Frame browser */}
        <div className="p-4">
          <h3 className="font-semibold mb-3">Choose Frame</h3>

          {/* Search */}
          <div className="relative mb-3">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search frames..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Category tabs */}
          <Tabs value={category} onValueChange={setCategory} className="mb-4">
            <TabsList className="flex-wrap h-auto gap-1">
              {FRAME_CATEGORIES.map((cat) => (
                <TabsTrigger key={cat.id} value={cat.id} className="text-xs">
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Frame grid */}
          <TemplateGrid
            templates={frames}
            selectedId={selectedArtwork.frame.templateId}
            onSelect={handleFrameSelect}
            columns={3}
            hasPremiumAccess={hasPremiumAccess}
            loading={false}
            emptyMessage="No frames in this category"
          />
        </div>

        {/* AI Recommendations */}
        <CollapsibleCard title="AI Suggest" collapsible={isMobile} className="mx-4 mb-4">
          <AIRecommendations />
        </CollapsibleCard>

        {/* Frame controls */}
        <CollapsibleCard title="Frame Settings" collapsible={isMobile} className="mx-4 mb-4">
          {/* Artwork size */}
          <div>
            <label className="text-xs text-muted-foreground">Artwork Size</label>
            <Slider
              value={selectedArtwork.displayScale ?? 1}
              min={0.5}
              max={2}
              step={0.05}
              onChange={(value) => updateDisplayScale(selectedArtwork.id, value)}
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Frame width */}
          <div>
            <label className="text-xs text-muted-foreground">Frame Width (mm)</label>
            <Slider
              value={selectedArtwork.frame.width}
              min={2}
              max={100}
              step={1}
              onChange={(value) => updateFrame(selectedArtwork.id, { width: value })}
              showValue
              formatValue={(v) => `${v}mm`}
            />
          </div>

          {/* Bevel highlight */}
          <div>
            <label className="text-xs text-muted-foreground">Highlight Intensity</label>
            <Slider
              value={selectedArtwork.frame.bevelAdjustments?.highlightIntensity ?? 0.3}
              min={0}
              max={1}
              step={0.05}
              onChange={(value) =>
                updateFrame(selectedArtwork.id, {
                  bevelAdjustments: {
                    ...selectedArtwork.frame.bevelAdjustments,
                    highlightIntensity: value,
                  },
                })
              }
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Bevel shadow */}
          <div>
            <label className="text-xs text-muted-foreground">Shadow Intensity</label>
            <Slider
              value={selectedArtwork.frame.bevelAdjustments?.shadowIntensity ?? 0.4}
              min={0}
              max={1}
              step={0.05}
              onChange={(value) =>
                updateFrame(selectedArtwork.id, {
                  bevelAdjustments: {
                    ...selectedArtwork.frame.bevelAdjustments,
                    shadowIntensity: value,
                  },
                })
              }
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Color hue shift */}
          <div>
            <label className="text-xs text-muted-foreground">Color Shift</label>
            <Slider
              value={selectedArtwork.frame.colorAdjustments?.hue ?? 0}
              min={-180}
              max={180}
              step={1}
              onChange={(value) =>
                updateFrame(selectedArtwork.id, {
                  colorAdjustments: {
                    ...selectedArtwork.frame.colorAdjustments,
                    hue: value,
                  },
                })
              }
              showValue
              formatValue={(v) => `${v}°`}
            />
          </div>
        </CollapsibleCard>

        {/* Shadow controls */}
        <CollapsibleCard title="Shadow Settings" collapsible={isMobile} className="mx-4 mb-4">
          <ShadowControls />
        </CollapsibleCard>

        {/* Price calculator */}
        <CollapsibleCard title="Price Estimate" collapsible={isMobile} className="mx-4 mb-4">
          <PriceCalculator />
        </CollapsibleCard>
      </div>
    </div>
  );
}
